import { Wrench } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const fixIcon = <Wrench className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />

export function FindingsTriageToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Triaging finding...'
      completedLabel='Finding triaged'
      completedDetail={pickString(state.args?.findingId)}
      completedIcon={fixIcon}
    />
  )
}

export function FindingsSearchToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const findings: Array<Record<string, unknown>> = Array.isArray(state.result)
    ? (state.result as Array<Record<string, unknown>>)
    : Array.isArray(state.result?.findings)
      ? state.result.findings
      : Array.isArray(state.result?.items)
        ? state.result.items
        : []
  const count =
    typeof state.result?.count === 'number'
      ? state.result.count
      : findings.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching findings...'
        completedLabel={
          count !== null
            ? `Found ${plural(count, 'finding')}`
            : 'Findings search complete'
        }
        loadingDetail={pickString(state.args?.query, state.args?.severity)}
        completedDetail={
          count === 0
            ? null
            : pickString(state.args?.query, state.args?.severity)
        }
        completedIcon={fixIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          columns={['Name', 'Severity', 'Status', 'Control']}
          emptyLabel='No matching findings'
          items={findings.map((finding) => ({
            id: pickString(finding.findingKey, finding.key, finding.id),
            title:
              pickString(finding.title, finding.summary) ?? 'Untitled finding',
            meta: [
              finding.severity,
              finding.status,
              finding.displayCode ??
                finding.controlRef ??
                finding.frameworkControlRef,
            ],
            tone: normalizeTone(finding.severity),
          }))}
        />
      ) : null}
    </>
  )
}

export function FindingsGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading finding details...'
        completedLabel='Finding details loaded'
        completedDetail={pickString(state.args?.findingId)}
        completedIcon={fixIcon}
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `finding-${
              pickString(
                state.result.findingKey,
                state.result.key,
                state.args?.findingId
              ) ?? 'detail'
            }`
          )}
          payload={{
            entityType: 'finding',
            toolName: 'findingsGet',
            title:
              pickString(state.result.title, state.result.summary) ??
              'Untitled finding',
            subtitle: pickString(
              state.result.findingKey,
              state.result.key,
              state.args?.findingId
            ),
            description: pickString(
              state.result.description,
              state.result.remediationGuidance
            ),
            fields: [
              {
                label: 'Severity',
                value: formatToolValue(state.result.severity),
                tone: normalizeTone(state.result.severity),
              },
              {
                label: 'Status',
                value: formatToolValue(state.result.status),
              },
              {
                label: 'Control',
                value: pickString(
                  state.result.displayCode,
                  state.result.controlRef,
                  state.result.frameworkControlRef
                ),
              },
              {
                label: 'Source',
                value: formatToolValue(state.result.sourceType),
              },
              {
                label: 'Ticket',
                value: formatToolValue(state.result.ticketId),
              },
            ],
            sections: [
              {
                title: 'Remediation guidance',
                content: formatToolValue(state.result.remediationGuidance),
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function FindingsVerifyToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Verifying remediation...'
      completedLabel='Remediation verified'
      completedDetail={pickString(state.args?.findingId)}
      completedIcon={fixIcon}
    />
  )
}

export function FindingsCloseToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Closing finding...'
      completedLabel='Finding closed'
      completedDetail={pickString(state.args?.findingId)}
      completedIcon={fixIcon}
    />
  )
}

export function FindingsUpdateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Updating finding...'
      completedLabel='Finding updated'
      completedDetail={pickString(state.args?.findingId)}
      completedIcon={fixIcon}
    />
  )
}

export function FindingsAcceptRiskToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Accepting risk...'
      completedLabel='Risk accepted'
      completedDetail={pickString(state.args?.findingId)}
      completedIcon={fixIcon}
    />
  )
}

export function FindingsFalsePositiveToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Marking as false positive...'
      completedLabel='Marked as false positive'
      completedDetail={pickString(state.args?.findingId)}
      completedIcon={fixIcon}
    />
  )
}
